import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

interface Props {
  open: boolean;
  onOpenChange: (o: boolean) => void;
}

export function InviteTokenModal({ open, onOpenChange }: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [value, setValue] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const raw = value.trim();
    if (!raw) return;

    // Accept either a full URL or a raw token
    let token = raw;
    try {
      if (raw.startsWith('http')) {
        const url = new URL(raw);
        const parts = url.pathname.split('/').filter(Boolean);
        token = parts[parts.length - 1] || raw;
      }
    } catch {
      // fall back to raw
    }

    if (!token || token.length < 8) {
      toast.error(t('onboarding.invalidInviteToken'));
      return;
    }

    onOpenChange(false);
    navigate(`/invite/${token}`);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('onboarding.inviteModalTitle')}</DialogTitle>
          <DialogDescription>{t('onboarding.inviteModalDesc')}</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="invite-token">{t('onboarding.inviteTokenLabel')}</Label>
            <Input
              id="invite-token"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder={t('onboarding.inviteTokenPlaceholder')}
              autoFocus
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              {t('common.cancel')}
            </Button>
            <Button type="submit">{t('onboarding.openInvite')}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
